import sum from 'lodash/sum'
import every from 'lodash/every'
import { mapState } from 'vuex'
import moment from 'moment'
import goTo from 'vuetify/lib/services/goto'
import { EventBus } from '../plugins/event-bus'
import RadarChart from '@/components/common/charts/RadarChart.vue'
import CurationGroupTitle from '@/components/photo/curate/CurationGroupTitle.vue'
import { displayConfirm } from '@/utils/swal'
import types from '@/utils/types'

export const curationMixin = {
  filters: {
    timeago: date => {
      return moment.utc(date).local().fromNow()
    }
  },
  components: {
    RadarChart,
    CurationGroupTitle
  },
  props: {
    me: {
      type: [Object, Boolean],
      default: () => {}
    },
    photo: {
      type: Object,
      default: () => {}
    },
    info: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      types,
      voteForElite: false,
      compositionRating: 0,
      techniqueRating: 0,
      creativityRating: 0,
      contentRating: 0,
      lightingRating: 0,
      valid: false,
      comment: '',
      commentRules: [
        v =>
          v.length < 1 ||
          v.length >= 60 ||
          'Your comment should be at least 60 characters length',
        v =>
          v.length < 4000 ||
          'Your comment is too long, maximum characters length allowed is 4000'
      ],
      processing: false,
      photoDetailsVisible: true,
      radarChartOptions: {
        legend: false,
        tooltips: {
          enabled: false
        },
        scale: {
          angleLines: {
            display: false
          },
          ticks: {
            min: 0,
            max: 5,
            stepSize: 1
          }
        }
      },
      earnableTokens: 16,
      commentDialog: false,
      curationGroupTitleData: {
        composition: {
          title: 'Composition',
          description:
            'How all the visual elements harmoniously express the purpose or intent of the image. Does the photo draw the viewer in to look where the creator intended?'
        },
        technique: {
          title: 'Technique',
          description:
            'The approach used to create the image. Printing, lighting, posing, capture, presentation media, and more are part of the technique applied to an image.'
        },
        creativity: {
          title: 'Creativity',
          description:
            'How the photographer was able to convey their idea, message or thought in an original and imaginative way through their lens.'
        },
        content: {
          title: 'Content',
          description:
            'What "values" the picture expresses, regardless of the appearance of the subject matter. You might appreciate the forms in a picture aesthetically, while the content might move you emotionally.'
        },
        lighting: {
          title: 'Lighting',
          description:
            'How the photographer was able to use and control light. Was the lighting applied in the photo (artificial or natural) properly used to enhance the image?'
        }
      }
    }
  },
  computed: {
    ...mapState({
      curationInterval: state => state.account.curationInterval,
      bonusTokens: state => state.account.bonusTokens
    }),
    commentLengthTokens() {
      let tokens = 0
      if (this.comment) {
        if (this.comment.length >= 60) {
          tokens = 2
        }
        if (this.comment.length >= 120) {
          tokens = 4
        }
        if (this.comment.length >= 400) {
          tokens = 8
        }
      }
      return tokens
    },
    inCuration() {
      return this.photo.statusName === types.photo.status.inCuration
    },
    progress() {
      const chars = 100
      const mult = 1.6666
      return Math.min(chars, this.comment.length * mult)
    },
    showHideDetailsText() {
      return this.photoDetailsVisible ? 'Hide' : 'Show'
    },
    availableRatingPoints() {
      let points = 25
      points = points - (this.compositionRating || 0)
      points = points - (this.techniqueRating || 0)
      points = points - (this.creativityRating || 0)
      points = points - (this.contentRating || 0)
      points = points - (this.lightingRating || 0)
      return points
    },
    averageCurationPoints() {
      const group = [
        this.compositionRating || 0,
        this.techniqueRating || 0,
        this.creativityRating || 0,
        this.contentRating || 0,
        this.lightingRating || 0
      ]
      const total = sum(group)
      let deductions = 0
      for (let i = 0; i < group.length; i += 1) {
        const n = group[i]
        if (n === 1) {
          deductions += 0.9
        }
        if (n === 2) {
          deductions += 0.6
        }
        if (n === 3) {
          deductions += 0.2
        }
      }
      const avg = total / (5 + deductions)
      return avg
    },
    averageCurationPercentage() {
      return this.averageCurationPoints * 20
    },
    averageCurationData() {
      const data = {
        text: '',
        color: ''
      }
      if (this.averageCurationPercentage < 25) {
        data.text = 'Poor'
        data.color = 'red'
      } else if (
        this.averageCurationPercentage >= 25 &&
        this.averageCurationPercentage < 40
      ) {
        data.text = 'Not too bad'
        data.color = 'orange'
      } else if (
        this.averageCurationPercentage >= 40 &&
        this.averageCurationPercentage < 65
      ) {
        data.text = 'Fair'
        data.color = 'yellow'
      } else if (
        this.averageCurationPercentage >= 65 &&
        this.averageCurationPercentage < 85
      ) {
        data.text = 'Good'
        data.color = 'lime darken-2'
      } else {
        data.text = 'Excellent'
        data.color = 'green'
      }
      return data
    },
    displayOverallValuation() {
      const value = every(this.ratingGroup)
      return value
    },
    ratingGroup() {
      return [
        this.compositionRating,
        this.techniqueRating,
        this.creativityRating,
        this.contentRating,
        this.lightingRating
      ]
    },
    radarChartData() {
      return {
        labels: [
          'Composition',
          'Technique',
          'Creativity',
          'Content',
          'Lighting'
        ],
        datasets: [
          {
            label: 'Curation',
            backgroundColor: 'rgba(1, 160, 255, 0.5)',
            data: this.ratingGroup
          }
        ]
      }
    },
    getBonusCommentTokens() {
      switch (this.photo.numberOfComments) {
        case 0:
          return 6
        case 1:
          return 4
        case 2:
          return 2
        default:
          return 0
      }
    },
    totalTokensForCuration() {
      let start = 0
      const isValidRating = every(this.ratingGroup)
      if (isValidRating) {
        start += 2
      }
      if (this.commentLengthTokens > 0) {
        start += this.getBonusCommentTokens
        start += this.commentLengthTokens
      }
      return start
    }
  },
  methods: {
    noop() {},
    setAllowKeyboardNavigation(value) {
      EventBus.$emit('set-allow-keyboard-navigation', value)
    },
    curate(type = null) {
      if (
        !this.me.isAdmin &&
        this.me.role !== types.user.role.curator &&
        this.curationInterval &&
        moment(this.curationInterval).add(10, 'seconds').isAfter(moment())
      ) {
        EventBus.$emit('snackbar', {
          text: 'Please take some extra time between curations (min 10 seconds)',
          color: 'info'
        })
        return
      }
      const isValidRating = every(this.ratingGroup)
      if (!isValidRating) {
        EventBus.$emit('snackbar', {
          text: 'Please rate all criteria',
          color: 'info'
        })
        return
      }
      const data = {
        voteForElite: type === 'publish_photo' && this.voteForElite,
        compositionRating: this.compositionRating,
        techniqueRating: this.techniqueRating,
        creativityRating: this.creativityRating,
        contentRating: this.contentRating,
        lightingRating: this.lightingRating,
        averageRating: this.averageCurationPoints,
        photo: this.photo.id,
        user: this.me.id,
        body: this.comment.trim(),
        type
      }
      if (this.$refs.form.validate()) {
        if (type === 'reject_photo') {
          displayConfirm(
            `
              <div class="text-left">
                <div class="mb-3">
                  Please only demote this photo if one of the following applies:
                </div>
                <ul>
                  <li>Pornography (explicit sexual content)</li>
                  <li>Suspiction of fraudulent agreements to promote the photo (favouritism)</li>
                  <li>Unacceptable technical quality</li>
                </ul>
              </div>
            `,
            result => {
              if (result.isConfirmed) {
                this.curateApiCall(data)
              }
            }
          )
        } else {
          this.curateApiCall(data)
        }
      } else {
        EventBus.$emit('snackbar', {
          text: 'Please enter a valid comment',
          color: 'error'
        })
      }
    },
    async curateApiCall(data) {
      const earningTokens = this.totalTokensForCuration
      try {
        this.processing = true
        this.$store.commit('account/SET_BONUS_TOKENS', 0)
        await this.$store.dispatch('comments/postCurate', data)
        await this.$store.dispatch('photos/getInfo', this.photo.id)
        await this.$store.dispatch('photos/getNumberOfPhotosInCuration')
        await this.$auth.fetchUser()
        this.$store.commit('account/SET_CURATION_INTERVAL', moment(new Date()))
        EventBus.$emit('snackbar', {
          text: `This photo has been curated successfully${
            earningTokens > 0 && this.inCuration
              ? ` and you earned ${earningTokens} Tokens!`
              : ''
          }`,
          color: 'success'
        })
        EventBus.$emit('photo:curated', this.photo.id)
      } catch (error) {
        this.$asa.appInsights('Mixins: curationMixin.js - method: curateApiCall', error)
      } finally {
        this.processing = false
        this.voteForElite = false
      }
    },
    showHidePhotoDetails() {
      this.photoDetailsVisible = !this.photoDetailsVisible
      EventBus.$emit('arePhotoDetailsVisible', this.photoDetailsVisible)
      if (!this.photoDetailsVisible) {
        setTimeout(() => {
          const el = document.getElementById('curate')
          if (el) {
            goTo(el, {
              offset: 250
            })
            // this.$ga.event({
            //   eventCategory: 'tracking',
            //   eventAction: 'actions',
            //   eventLabel: 'show/hide details',
            //   eventValue: true
            // })
          }
        })
      }
    },
    isButtonDisabled(i, model) {
      return i > model && model + this.availableRatingPoints < i
    },
    onPaste(event) {
      const canPaste =
        this.me && (this.me.isAdmin || this.me.role === types.user.role.curator)
      if (!canPaste) {
        event.preventDefault()
        EventBus.$emit('snackbar', {
          text: 'Copy and paste function is disabled for comments.',
          color: 'error',
          timeout: 0
        })
      }
    }
  }
}
